<template>
  <div>
    <div v-show="assets.length > 0" class="scrollable pa-0 ma-0 subtitle-assets-list" v-bind="$attrs">
      <v-row v-for="(asset, assetIdx) in assets" :key="asset.id" density="comfortable" no-gutters data-cy="podMenu-subtitles-assetFile">
        <v-col cols="8" data-cy="podMenu-subtitles-assetName">
          <v-radio-group v-model="selectedAssetId" hide-details >
            <v-radio :label="asset.name" :value="asset.id" ></v-radio>
          </v-radio-group>
        </v-col>
        <v-col cols="2" class="d-flex justify-center align-center text-uppercase">
          <span style="margin-top:5px;">
            {{ getTypeText(asset.type) }}
          </span>
        </v-col>
        <v-col cols="2" class="d-flex justify-center align-center" style="padding-top: 3px;">
          <v-small-icon-btn
            ref="subtitleMenuActivator"
            @click="showMenu(assetIdx)"
            :aria-label="$t('routes.pod.menuSubtitles.subtitleActions')"
          >
            <v-icon data-cy="podMenu-subtitles-assetActionsButton">mdi-dots-vertical</v-icon>
          </v-small-icon-btn>
        </v-col>
      </v-row>
    </div>

    <v-menu v-model="isAssetMenuVisible" :target="assetMenuTarget">
      <v-list data-cy="podFiles-subtitles-assetActions">
        <v-list-item @click="isRenameAssetDialogVisible = true">
          <v-list-item-title>
            <v-icon>mdi-rename-box</v-icon>
            {{ $t('common.rename') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-on:click="isDeletePodDialogVisible = true" v-if="$canDeleteNodeAndPod()">
          <v-list-item-title>
            <v-icon>mdi-trash-can-outline</v-icon>
            {{ $t('common.delete') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-on:click="isTranslateSubtitleDialogVisible = true">
          <v-list-item-title>
            <v-icon>mdi-translate-variant</v-icon>
            {{ $t('routes.pod.menuTranslateSubtitles.translate') }}
          </v-list-item-title>
        </v-list-item>

        <v-list-item v-on:click="isImprintSubtitleDialogVisible = true">
          <v-list-item-title>
            <v-icon>mdi-movie-open-plus-outline</v-icon>
            {{ $t('routes.pod.menuImprintSubtitles.imprintSubtitles') }}
          </v-list-item-title>
        </v-list-item>
      </v-list>
    </v-menu>

    <RenameAssetDialog
      v-if="$canControlNodeAndPod()"
      v-model="isRenameAssetDialogVisible"
      :pod="pod"
      :asset="dialogActiveAsset"
      data-cy="podFiles-subtitles-renameAsset"
    />

    <DeletePodDialog
      v-if="$canDeleteNodeAndPod()"
      v-model="isDeletePodDialogVisible"
      :pod="pod"
      :asset="dialogActiveAsset"
      data-cy="podFiles-subtitles-deleteAsset"
    />

    <TranslateSubtitleAssetDialog
      v-if="$canRunPaidService() && (isVideoPod || isAudioPod)"
      v-model="isTranslateSubtitleDialogVisible"
      :asset="dialogActiveAsset"
      data-cy="podFiles-subtitles-translateAsset"
    />

    <ImprintSubtitleDialog
      v-if="$canRunFreeService() && isVideoPod"
      v-model="isImprintSubtitleDialogVisible"
      :asset="dialogActiveAsset"
      data-cy="podFiles-subtitles-imprintAsset"
    />
  </div>
</template>

<script>
import { usePodStore } from '@/stores/PodStore.js';
import { useSubtitleStore } from '@/stores/SubtitleStore.js'
import TranslateSubtitleAssetDialog from './dialogs/TranslateSubtitleAssetDialog.vue'
import ImprintSubtitleDialog from './dialogs/ImprintSubtitleDialog.vue'

import RenameAssetDialog from '../assetsToolbar/dialogs/RenameAssetDialog.vue'
import DeletePodDialog from '../../../../../routes/nodes2/components/dialogs/DeletePodDialog.vue'

export default {
  emits: ["setAssetForEditing", "update:modelValue"],
  inheritAttrs: false,
  name: "PodSubtitleAssetsList",
  components: {
    TranslateSubtitleAssetDialog,
    ImprintSubtitleDialog,
    RenameAssetDialog,
    DeletePodDialog,
  },
  props: {
    assets: Array,
    modelValue: [String, Number]
  },
  data: function() {
    return {
      selectedAssetId: null,
      previousSelectedAssetId: null,
      disableEditing: null,
      timeoutHandler: null,

      isRenameAssetDialogVisible: false,
      isDeletePodDialogVisible: false,
      isTranslateSubtitleDialogVisible: false,
      isImprintSubtitleDialogVisible: false,
      isAssetMenuVisible: false,

      dialogActiveAsset: null,

      menuActiveAssetIdx: null,
      assetMenuTarget: null,
    };
  },

  watch: {
    selectedAssetId: function(value) {
      if (this.timeoutHandler !== null) {
        clearTimeout(this.timeoutHandler);
      }

      if (this.previousSelectedAssetId !== value) {
        this.timeoutHandler = setTimeout(() => {
          // Emit the update:modelValue event for v-model binding
          this.$emit('update:modelValue', value);
          this.setEditItem(value);
          this.previousSelectedAssetId = value;
        }, 200);
      }
    },
    modelValue: {
      immediate: true,
      handler(newVal) {
        if (newVal && newVal !== this.selectedAssetId) {
          this.selectedAssetId = newVal;
        }
      }
    },
    assets: function() {
      this.hideMenu();
    },
    isRenameAssetDialogVisible: function (isVisible) {
      this.toggleDialogVisibility(isVisible);
    },
    isDeletePodDialogVisible: function (isVisible) {
      this.toggleDialogVisibility(isVisible);
    },
    isImprintSubtitleDialogVisible: function (isVisible) {
      this.toggleDialogVisibility(isVisible);
    },
    isTranslateSubtitleDialogVisible: function (isVisible) {
      this.toggleDialogVisibility(isVisible);
    },
  },

  mounted() {
    // Adds event listener to close menu when scrolling
    const nav = document.querySelector('.v-navigation-drawer__content');
    nav && nav.addEventListener('scroll', this.hideMenu);

    if (this.selectedSubtitleAsset) {
      this.selectedAssetId = this.selectedSubtitleAsset.id;
    }
  },

  beforeUnmount() {
    // Removes event listener to close menu when scrolling
    const nav = document.querySelector('.v-navigation-drawer__content');
    nav && nav.removeEventListener('scroll', this.hideMenu);
  },

  methods: {
    stub: function () {
      this.showingMenu = false;
    },
    setEditItem: function(item) {
      if (this.disableEditing) {
        console.log('Editing is disabled');
        return;
      }

      this.$emit('setAssetForEditing', item);
    },
    getTypeText(type) {
      switch (type) {
        case 6:
          return 'vtt';
        case 7:
          return 'srt';
        default:
          return '';
      }
    },
    hideMenu() {
      this.assetMenuTarget = null;
      this.isAssetMenuVisible = false;
      this.menuActiveAssetIdx = null;
    },
    showMenu(assetIdx) {
      this.assetMenuTarget = this.$refs.subtitleMenuActivator[assetIdx];
      this.isAssetMenuVisible = true;
      this.menuActiveAssetIdx = assetIdx;
    },
    toggleDialogVisibility(isVisible) {
      if (isVisible) {
        this.dialogActiveAsset = this.assets[this.menuActiveAssetIdx];
      }

      this.hideMenu();
    }
  },

  computed: {
    pod: function() {
      const podStore = usePodStore();
      return podStore.getViewPod;
    },
    isVideoPod: function() {
      const podStore = usePodStore();
      return podStore.isVideoPod;
    },
    isAudioPod: function() {
      const podStore = usePodStore();
      return podStore.isAudioPod;
    },
    selectedSubtitleAsset: function () {
      const subtitleStore = useSubtitleStore();
      return subtitleStore.getSelectedAsset;
    },
  }
};
</script>
